import { useQuery } from '@tanstack/react-query';
import {
  contributorRankingGraphql,
  getEngineeringStatsGraphql,
  getProductStatsGraphql,
  getReworkDensityGraphql,
  getWorkflowStageTimeGraphql,
  getReportQuestionDetailsGraphql,
  getReportQuestionsGraphql,
  getTeamDashboardDetailsGraphql,
  issueTimelineDistributionGraphql,
  scopeChangeMetricGraphql,
  sprintGraphql,
  teamHygieneTicketDetailsGraphql,
} from './query';

const QUERY_KEYS = {
  reportQuestions: 'reportQuestions',
  reportDetails: 'reportDetails',
  sprintMetricByType: 'sprintMetricByType',
  scopeChange: 'scopeChange',
  issueBugDist: 'issueBugDist',
  contributorRanking: 'contributorRanking',
  teamDashboardDetails: 'teamDashboardDetails',
  teamHygieneTicketDetails: 'teamHygieneTicketDetails',
  engineeringStats: 'engineeringStats',
  productStats: 'productStats',
  reworkDensity: 'reworkDensity',
  workflowStageTime: 'workflowStageTime',
};

export const useReportQuestionsGql = (pageNumber: number, pageSize: number) => {
  return useQuery(
    [QUERY_KEYS.reportQuestions, pageNumber, pageSize],
    () => getReportQuestionsGraphql(pageNumber, pageNumber),
    {
      select: (data) => data.ReportQuestionDetails.data,
    }
  );
};

export const useReportQuestionDetailsGql = (
  reportId: string,
  startDate: string,
  endDate: string,
  sprintId: string
) => {
  return useQuery(
    [QUERY_KEYS.reportQuestions, reportId, startDate, endDate, sprintId],
    () =>
      getReportQuestionDetailsGraphql(reportId, startDate, endDate, sprintId),
    {
      select: (data) => data.ReportDetails,
      enabled: !!startDate && !!endDate && reportId !== '20',
    }
  );
};

export const useSprintMetricByTypeGql = (
  metric: string,
  teamId: string,
  startDate: string,
  endDate: string,
  sprintId: string
) => {
  return useQuery(
    [
      QUERY_KEYS.sprintMetricByType,
      metric,
      teamId,
      startDate,
      endDate,
      sprintId,
    ],
    () => sprintGraphql(metric, teamId, startDate, endDate, sprintId),
    {
      select: (data) => {
        return {
          ...data.SprintMetricsByType,
          data: JSON.parse(data.SprintMetricsByType.data),
        };
      },
      enabled: !!startDate && !!endDate,
    }
  );
};

export const useScopeChangeMetricGql = (
  metric: string,
  teamId: string,
  startDate: string,
  endDate: string,
  sprintId: string
) => {
  return useQuery(
    [QUERY_KEYS.scopeChange, metric, teamId, startDate, endDate, sprintId],
    () =>
      scopeChangeMetricGraphql(metric, teamId, startDate, endDate, sprintId),
    {
      select: (data) => {
        return {
          ...data?.SprintMetricsByType,
          data: JSON.parse(data?.SprintMetricsByType?.data),
        };
      },
      enabled: !!startDate && !!endDate,
    }
  );
};

export const useIssueBugDistGql = (
  startDate: string,
  endDate: string,
  sprintId: string,
  teamId: string
) => {
  return useQuery(
    [QUERY_KEYS.issueBugDist, startDate, endDate, teamId, sprintId],
    () =>
      issueTimelineDistributionGraphql(
        startDate,
        endDate,
        teamId,
        sprintId,
        'all'
      ),
    {
      select: (data) => {
        return {
          ...data.CustomReportMetricByType,
          data: JSON.parse(data.CustomReportMetricByType.data),
        };
      },
      enabled: !!startDate && !!endDate,
    }
  );
};

export const useContributorRankingGql = (
  startDate: string,
  endDate: string,
  sprintId: string,
  teamId: string,
  jobRole: string
) => {
  return useQuery(
    [
      QUERY_KEYS.contributorRanking,
      startDate,
      endDate,
      teamId,
      sprintId,
      jobRole,
    ],
    () =>
      contributorRankingGraphql(
        startDate,
        endDate,
        teamId,
        sprintId,
        'CONTRIBUTOR_PERFORMANCE',
        jobRole
      ),
    {
      select: (data) => {
        return {
          ...data.CustomReportMetricByType,
          data: JSON.parse(data.CustomReportMetricByType.data),
        };
      },
      enabled: !!startDate && !!endDate,
    }
  );
};

export const useTeamDashboardDetailsGql = (
  filter: {
    startDate: string;
    endDate: string;
    teamId?: string;
    sprintId?: string;
  },
  pageNumber: number,
  pageSize: number
) => {
  return useQuery(
    [
      QUERY_KEYS.teamDashboardDetails,
      filter.startDate,
      filter.endDate,
      filter.teamId,
      filter.sprintId,
      pageNumber,
      pageSize,
    ],
    () => getTeamDashboardDetailsGraphql(filter, pageNumber, pageSize),
    {
      select: (data) => data.TeamDashboardDetails,
      enabled: !!filter.startDate && !!filter.endDate,
    }
  );
};

export const useTeamHygieneTicketDetailsGql = (
  startDate: string,
  endDate: string,
  issueCategory: string,
  filter: {
    teamId?: string;
    sprintId?: string;
  },
  pageNumber: number,
  pageSize: number
) => {
  return useQuery(
    [
      QUERY_KEYS.teamHygieneTicketDetails,
      startDate,
      endDate,
      issueCategory,
      filter.teamId,
      filter.sprintId,
      pageNumber,
      pageSize,
    ],
    () =>
      teamHygieneTicketDetailsGraphql(
        startDate,
        endDate,
        issueCategory,
        filter,
        pageNumber,
        pageSize
      ),
    {
      select: (data) => data.TeamHygieneTicketDetails,
      enabled: !!startDate && !!endDate && !!issueCategory,
    }
  );
};

export const useEngineeringStatsGql = (
  filter: {
    startDate: string;
    endDate: string;
    teamIds: string[];
    sprintId: string;
  },
  pageNumber: number,
  pageSize: number
) => {
  return useQuery(
    [
      QUERY_KEYS.engineeringStats,
      filter.startDate,
      filter.endDate,
      filter.teamIds,
      filter.sprintId,
      pageNumber,
      pageSize,
    ],
    () => getEngineeringStatsGraphql(filter, pageNumber, pageSize),
    {
      select: (data) => data.EngineeringStats,
      enabled: !!filter.startDate && !!filter.endDate,
    }
  );
};

export const useProductStatsGql = (
  filter: {
    startDate: string;
    endDate: string;
    teamIds: string[];
    sprintId: string;
  },
  pageNumber: number,
  pageSize: number
) => {
  return useQuery(
    [
      QUERY_KEYS.productStats,
      filter.startDate,
      filter.endDate,
      filter.teamIds,
      filter.sprintId,
      pageNumber,
      pageSize,
    ],
    () => getProductStatsGraphql(filter, pageNumber, pageSize),
    {
      select: (data) => data.ProductStats,
      enabled: !!filter.startDate && !!filter.endDate,
    }
  );
};

export const useWorkflowStageTimeGql = (filter: {
  startDate: string;
  endDate: string;
  teamId?: string;
  sprintId?: string;
  epicKey?: string;
  author?: string;
}) => {
  return useQuery(
    [
      QUERY_KEYS.workflowStageTime,
      filter.startDate,
      filter.endDate,
      filter.teamId,
      filter.sprintId,
      filter.epicKey,
      filter.author,
    ],
    () => getWorkflowStageTimeGraphql(filter),
    {
      select: (data) => {
        const raw = data.WorkflowStageTimeByComponent;
        return {
          ...raw,
          data: raw.data ? JSON.parse(raw.data) : [],
        };
      },
      enabled: !!filter.startDate && !!filter.endDate,
    }
  );
};

export const useReworkDensityGql = (
  filter: {
    startDate: string;
    endDate: string;
    teamIds: string[];
    sprintId: string;
    projectsFilter?: string[];
  },
  pageNumber: number,
  pageSize: number
) => {
  return useQuery(
    [
      QUERY_KEYS.reworkDensity,
      filter.startDate,
      filter.endDate,
      filter.teamIds,
      filter.sprintId,
      filter.projectsFilter,
      pageNumber,
      pageSize,
    ],
    () => getReworkDensityGraphql(filter, pageNumber, pageSize),
    {
      select: (data) => data.ReworkDensity,
      enabled: !!filter.startDate && !!filter.endDate,
    }
  );
};
